/** @jsxImportSource @emotion/react */
import { useContext, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import type { ReactElement, ReactNode } from 'react';
import { Formik, Form } from 'formik';
import yup from 'utils/yup';
import ContactUsModal from 'components/Brief/ArtifactNavbar/ContactUsModal';
import ValidatedFormikField from 'components/Utils/Forms/ValidatedFormikField';
import useToggle from 'hooks/useToggle';
import { verifyAndLinkWithLocalAccount } from 'api-client/accounts';
import MatchingAccountCard from './MatchingAccountCard';
import {
  SmartLoginStateContext,
  SmartLoginActionsContext,
  FormStepEnum,
} from './context/SmartLoginContextProvider';
import { ODSAlert, ODSButton, ODSGrid, ODSGroup } from 'bc-ods';

type LinkError =
  | { type: 'rate-limited' }
  | { type: 'locked' }
  | { type: 'already-linked' }
  | { type: 'deactivated' }
  | null;

const messages = defineMessages({
  verifyYourIdentity: {
    id: 'SSOVerifyExistingAccount.verifyYourIdentity',
    defaultMessage: 'Verify your identity',
  },
  enterPassword: {
    id: 'SSOVerifyExistingAccount.enterPassword',
    defaultMessage:
      'Enter the password for your existing account to link it with your SSO identity.',
  },
  emailLabel: {
    id: 'SSOVerifyExistingAccount.emailLabel',
    defaultMessage: 'Email',
  },
  password: {
    id: 'SSOVerifyExistingAccount.password',
    defaultMessage: 'Password',
  },
  forgotPassword: {
    id: 'SSOVerifyExistingAccount.forgotPassword',
    defaultMessage: 'Forgot password',
  },
  help: {
    id: 'SSOVerifyExistingAccount.help',
    defaultMessage: 'Help',
  },
  buttonBack: {
    id: 'SSOVerifyExistingAccount.buttonBack',
    defaultMessage: 'Back',
  },
  linkAccount: {
    id: 'SSOVerifyExistingAccount.linkAccount',
    defaultMessage: 'Link account',
  },
  linkingUnavailableHeading: {
    id: 'SSOVerifyExistingAccount.linkingUnavailableHeading',
    defaultMessage: 'Linking temporarily unavailable',
  },
  linkingUnavailableBody: {
    id: 'SSOVerifyExistingAccount.linkingUnavailableBody',
    defaultMessage:
      'This account was recently linked to another account. Linking is temporarily unavailable for security.',
  },
  linkingUnavailableAlert: {
    id: 'SSOVerifyExistingAccount.linkingUnavailableAlert',
    defaultMessage:
      'Try logging in after one day or contact your customer representative for support.',
  },
  accountLockedHeading: {
    id: 'SSOVerifyExistingAccount.accountLockedHeading',
    defaultMessage: 'Account temporarily locked',
  },
  accountLockedBody: {
    id: 'SSOVerifyExistingAccount.accountLockedBody',
    defaultMessage: 'Your account has been temporarily locked for security.',
  },
  accountLockedAlert: {
    id: 'SSOVerifyExistingAccount.accountLockedAlert',
    defaultMessage: 'This account is temporarily locked. Try again in 15 minutes, or reset your password.',
  },
  accountUnavailableHeading: {
    id: 'SSOVerifyExistingAccount.accountUnavailableHeading',
    defaultMessage: 'Account unavailable',
  },
  accountUnavailableBody: {
    id: 'SSOVerifyExistingAccount.accountUnavailableBody',
    defaultMessage:
      "The account you're trying to link is already connected to a different SSO identity. Only one SSO identity can be linked per account.",
  },
  accountUnavailableAlert: {
    id: 'SSOVerifyExistingAccount.accountUnavailableAlert',
    defaultMessage:
      'Try linking to a different account or contact your customer representative to unlink this account and find the correct one.',
  },
  accountDeactivatedHeading: {
    id: 'SSOVerifyExistingAccount.accountDeactivatedHeading',
    defaultMessage: 'Account deactivated',
  },
  accountDeactivatedBody: {
    id: 'SSOVerifyExistingAccount.accountDeactivatedBody',
    defaultMessage:
      'Your account has been deactivated by a customer representative. You are unable to access Onebrief at this time.',
  },
  accountDeactivatedAlert: {
    id: 'SSOVerifyExistingAccount.accountDeactivatedAlert',
    defaultMessage:
      'If you believe this is a mistake, contact your customer representative for support.',
  },
  linkDifferentAccount: {
    id: 'SSOVerifyExistingAccount.linkDifferentAccount',
    defaultMessage: 'Link a different account',
  },
  resetPassword: {
    id: 'SSOVerifyExistingAccount.resetPassword',
    defaultMessage: 'Reset password',
  },
});

function getLinkErrorDisplay(linkError: NonNullable<LinkError>): {
  heading: ReactNode;
  body: ReactNode;
  alertColor: string;
  alertContent: ReactNode;
} {
  switch (linkError.type) {
    case 'rate-limited':
      return {
        heading: <FormattedMessage {...messages.linkingUnavailableHeading} />,
        body: <FormattedMessage {...messages.linkingUnavailableBody} />,
        alertColor: 'warning',
        alertContent: <FormattedMessage {...messages.linkingUnavailableAlert} />,
      };
    case 'locked':
      return {
        heading: <FormattedMessage {...messages.accountLockedHeading} />,
        body: <FormattedMessage {...messages.accountLockedBody} />,
        alertColor: 'danger',
        alertContent: <FormattedMessage {...messages.accountLockedAlert} />,
      };
    case 'already-linked':
      return {
        heading: <FormattedMessage {...messages.accountUnavailableHeading} />,
        body: <FormattedMessage {...messages.accountUnavailableBody} />,
        alertColor: 'warning',
        alertContent: <FormattedMessage {...messages.accountUnavailableAlert} />,
      };
    case 'deactivated':
      return {
        heading: <FormattedMessage {...messages.accountDeactivatedHeading} />,
        body: <FormattedMessage {...messages.accountDeactivatedBody} />,
        alertColor: 'warning',
        alertContent: <FormattedMessage {...messages.accountDeactivatedAlert} />,
      };
  }
}

const validationSchema = yup.object().shape({
  obPassword: yup.string().required('required').min(3, 'min'),
});

function SSOVerifyExistingAccount(): ReactElement {
  const [showError, setShowError] = useState(false);
  const [linkError, setLinkError] = useState<LinkError>(null);
  const { formError, profile, provider, matchingId, discoveredAccountDetails } = useContext(SmartLoginStateContext);
  const { setFormError, setFormStep, goToPreviousStep, formStepHistory, setDiscoveredAccountDetails } = useContext(SmartLoginActionsContext);
  const previousStep = formStepHistory[formStepHistory.length - 2];
  const [contactUsModalVisible, toggleContactUsModal] = useToggle();

  const handleVerify = async (values: { obPassword: string }) => {
    try {
      setShowError(false);
      setLinkError(null);
      const result = await verifyAndLinkWithLocalAccount(
        matchingId,
        matchingId,
        values.obPassword,
        provider
      );
      if (result.status === 200) {
        const data = result.data as any;
        setDiscoveredAccountDetails({
          avatarColor: data.avatarColor,
          rank: data.rank,
          jobTitle: data.jobTitle,
          organization: data.organization,
          parentOrganization: data.parentOrganization,
        });
        setFormStep(FormStepEnum.SSOLinkSuccess);
      } else if (result.status === 429) {
        setLinkError({ type: 'rate-limited' });
      } else {
        setFormError('An unexpected error has occurred. Please try again.');
        setShowError(true);
      }
    } catch (error: any) {
      const status = error.response?.status;
      if (status === 423) {
        setLinkError({ type: 'locked' });
      } else if (status === 409) {
        setLinkError({ type: 'already-linked' });
      } else if (status === 401 && !error.response?.data?.error) {
        setLinkError({ type: 'deactivated' });
      } else {
        const errorMessage = error.response?.data?.error;
        setFormError(errorMessage ?? 'An unexpected error has occurred. Please try again.');
        setShowError(true);
      }
    }
  };

  if (linkError) {
    const { heading, body, alertColor, alertContent } = getLinkErrorDisplay(linkError);

    return (
      <>
        <ODSGroup stacked data-testid="account-linking-dialog">
          <div>
            <h5 data-testid="link-error-heading">{heading}</h5>
            <p className="text-secondary mb-0" data-testid="link-error-body">
              {body}
            </p>
          </div>
          <ODSAlert className="d-flex align-items-center" color={alertColor} isOpen data-testid="link-error-alert">
            {alertContent}
          </ODSAlert>
          {previousStep === FormStepEnum.SSOAccountDiscovered && (
            <MatchingAccountCard profile={profile} discoveredAccountDetails={discoveredAccountDetails} />
          )}
          {linkError.type === 'already-linked' && (
            <ODSButton
              size="md"
              data-testid="link-different-account"
              onClick={() => setFormStep(FormStepEnum.SSOLinkAccount)}
            >
              <FormattedMessage {...messages.linkDifferentAccount} />
            </ODSButton>
          )}
          {linkError.type === 'locked' && (
            <ODSButton
              color="primary"
              size="md"
              data-testid="reset-password"
              onClick={() => setFormStep(FormStepEnum.Forgot)}
            >
              <FormattedMessage {...messages.resetPassword} />
            </ODSButton>
          )}
        </ODSGroup>
        <ContactUsModal isOpen={contactUsModalVisible} toggle={toggleContactUsModal} />
      </>
    );
  }

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        initialValues={{ email: matchingId ?? '', obPassword: '' }}
        onSubmit={handleVerify}
      >
        {({ isValid, isSubmitting }) => (
          <div data-testid="sso-verify-existing-account">
            <Form>
              <ODSGrid spacing="md" columns={1}>
                <div>
                  <h5>
                    <FormattedMessage {...messages.verifyYourIdentity} />
                  </h5>
                  <p className="text-secondary mb-0">
                    <FormattedMessage {...messages.enterPassword} />
                  </p>
                </div>
                {showError && (
                  <ODSAlert className="my-2 d-flex align-items-center" color="danger" isOpen={showError}>
                    {formError}
                  </ODSAlert>
                )}
                <ValidatedFormikField
                  name="email"
                  id="email"
                  size="md"
                  label={<FormattedMessage {...messages.emailLabel} />}
                  disabled
                  data-testid="email"
                  autoComplete="email"
                />
                <div className="gap-0">
                  <ValidatedFormikField
                    name="obPassword"
                    id="obPassword"
                    size="md"
                    label={
                      <span className="d-flex justify-content-end gap-2 align-items-center">
                        <span className="me-auto">
                          <FormattedMessage {...messages.password} />
                        </span>
                        <ODSButton
                          quiet
                          type="button"
                          data-testid="button-forgot-password"
                          css={(theme) => ({
                            color: theme.colors.primary,
                            textDecoration: 'underline',
                            fontSize: '0.75rem',
                            padding: 0,
                          })}
                          onClick={() => {
                            setShowError(false);
                            setFormStep(FormStepEnum.Forgot);
                          }}
                        >
                          <FormattedMessage {...messages.forgotPassword} />
                        </ODSButton>
                        <span className="text-secondary" aria-hidden>
                          |
                        </span>
                        <ODSButton
                          quiet
                          type="button"
                          data-testid="button-help"
                          css={(theme) => ({
                            color: theme.colors.primary,
                            textDecoration: 'underline',
                            fontSize: '0.75rem',
                            padding: 0,
                          })}
                          onClick={toggleContactUsModal}
                        >
                          <FormattedMessage {...messages.help} />
                        </ODSButton>
                      </span>
                    }
                    type="password"
                    autoFocus
                    data-testid="obPassword"
                  />
                </div>
                <div className="d-flex gap-2 mt-4">
                  <ODSButton
                    className="flex-shrink-0"
                    size="md"
                    quiet
                    type="button"
                    data-testid="button-back"
                    onClick={goToPreviousStep}
                  >
                    <FormattedMessage {...messages.buttonBack} />
                  </ODSButton>
                  <ODSButton
                    color="primary"
                    size="md"
                    className="flex-grow-1"
                    disabled={!isValid || isSubmitting}
                    type="submit"
                    data-testid="submit"
                  >
                    <FormattedMessage {...messages.linkAccount} />
                  </ODSButton>
                </div>
              </ODSGrid>
            </Form>
          </div>
        )}
      </Formik>
      <ContactUsModal isOpen={contactUsModalVisible} toggle={toggleContactUsModal} />
    </>
  );
}

export default SSOVerifyExistingAccount;
